// Onboarding tutorial v2 — interactive guided tutorial with progressive disclosure
import { t } from "../shared/i18n.ts";

const STORAGE_KEY = "smartpluvia-onboarding-done";
const PROGRESS_KEY = "smartpluvia-onboarding-step";

export interface OnboardingStep {
  title: () => string;
  description: () => string;
  targetSelector: string;
  /** If set, the step waits for this DOM event on the target before auto-advancing */
  waitForAction?: string;
  /** CSS class to pulse on the target for attention */
  highlightClass?: string;
}

const STEPS: OnboardingStep[] = [
  {
    title: () => t("onboarding.step1Title"),
    description: () => t("onboarding.step1Desc"),
    targetSelector: '[data-mode="boundary"]',
    waitForAction: "click",
    highlightClass: "onboarding-pulse",
  },
  {
    title: () => t("onboarding.step2Title"),
    description: () => t("onboarding.step2Desc"),
    targetSelector: "#activeModel",
  },
  {
    title: () => t("onboarding.step3Title"),
    description: () => t("onboarding.step3Desc"),
    targetSelector: "#canvasContainer",
  },
  {
    title: () => t("onboarding.step4Title"),
    description: () => t("onboarding.step4Desc"),
    targetSelector: '[data-mode="zone"]',
    waitForAction: "click",
    highlightClass: "onboarding-pulse",
  },
  {
    title: () => t("onboarding.step5Title"),
    description: () => t("onboarding.step5Desc"),
    targetSelector: '[data-mode="pipe"]',
    waitForAction: "click",
    highlightClass: "onboarding-pulse",
  },
  {
    title: () => t("onboarding.step6Title"),
    description: () => t("onboarding.step6Desc"),
    targetSelector: "#btnExportPDF",
  },
];

export { STEPS as ONBOARDING_STEPS };

let currentStep = 0;
let overlayEl: HTMLElement | null = null;
let tooltipEl: HTMLElement | null = null;
let actionCleanup: (() => void) | null = null;

/**
 * Check if onboarding has been completed.
 */
export function isOnboardingDone(): boolean {
  return localStorage.getItem(STORAGE_KEY) === "true";
}

/**
 * Get the last completed step index (-1 if not started).
 */
export function getOnboardingProgress(): number {
  const saved = localStorage.getItem(PROGRESS_KEY);
  return saved ? parseInt(saved, 10) : -1;
}

/**
 * Mark onboarding as done and clean up.
 */
function finishOnboarding(): void {
  localStorage.setItem(STORAGE_KEY, "true");
  localStorage.removeItem(PROGRESS_KEY);
  cleanup();
}

/**
 * Start the onboarding tutorial if not already completed.
 * Resumes from the last saved step if interrupted.
 */
export function startOnboarding(): void {
  if (isOnboardingDone()) return;

  const progress = getOnboardingProgress();
  currentStep = progress >= 0 ? Math.min(progress + 1, STEPS.length - 1) : 0;

  createOverlay();
  showStep(currentStep);
}

function createOverlay(): void {
  overlayEl = document.createElement("div");
  overlayEl.className = "onboarding-overlay";
  overlayEl.setAttribute("data-testid", "onboarding-overlay");
  document.body.appendChild(overlayEl);

  tooltipEl = document.createElement("div");
  tooltipEl.className = "onboarding-tooltip";
  tooltipEl.setAttribute("data-testid", "onboarding-tooltip");
  // Not aria-modal: most steps are completed by clicking a tool outside it.
  tooltipEl.setAttribute("role", "dialog");
  document.body.appendChild(tooltipEl);

  // Close on Escape
  document.addEventListener("keydown", onEscape);
}

function onEscape(e: KeyboardEvent): void {
  if (e.key === "Escape") finishOnboarding();
}

function cleanup(): void {
  cleanupAction();
  if (overlayEl) { overlayEl.remove(); overlayEl = null; }
  if (tooltipEl) { tooltipEl.remove(); tooltipEl = null; }
  document.removeEventListener("keydown", onEscape);

  // Remove any lingering pulse classes
  document.querySelectorAll(".onboarding-pulse").forEach((el) => {
    el.classList.remove("onboarding-pulse");
  });
}

function cleanupAction(): void {
  if (actionCleanup) {
    actionCleanup();
    actionCleanup = null;
  }
}

function showStep(index: number): void {
  if (index >= STEPS.length) {
    finishOnboarding();
    return;
  }

  cleanupAction();

  const step = STEPS[index];
  const target = document.querySelector(step.targetSelector);

  // Position highlight cutout
  if (target) {
    // A rail button can sit below the fold on a short window; bring it into
    // view (when its container scrolls) before measuring.
    target.scrollIntoView?.({ block: "nearest", inline: "nearest" });
    const rect = target.getBoundingClientRect();
    const padding = 6;
    // dynamic: cutout box is measured from the highlighted step target
    overlayEl!.style.setProperty("--cutout-top", `${rect.top - padding}px`);
    overlayEl!.style.setProperty("--cutout-left", `${rect.left - padding}px`);
    overlayEl!.style.setProperty("--cutout-width", `${rect.width + padding * 2}px`);
    overlayEl!.style.setProperty("--cutout-height", `${rect.height + padding * 2}px`);

    // Add pulse highlight
    if (step.highlightClass) {
      target.classList.add(step.highlightClass);
    }
  }

  // Build progress dots
  const dots = STEPS.map((_, i) => {
    const cls = i === index ? "onboarding-dot active" : i < index ? "onboarding-dot done" : "onboarding-dot";
    return `<span class="${cls}"></span>`;
  }).join("");

  // Build tooltip content
  tooltipEl!.innerHTML = `
    <div class="onboarding-progress" data-testid="onboarding-progress">${dots}</div>
    <div class="onboarding-step-indicator">${index + 1} / ${STEPS.length}</div>
    <h4 class="onboarding-title">${step.title()}</h4>
    <p class="onboarding-desc">${step.description()}</p>
    <div class="onboarding-actions">
      <button class="onboarding-btn onboarding-skip" data-testid="onboarding-skip">${t("onboarding.skip")}</button>
      <button class="onboarding-btn onboarding-next" data-testid="onboarding-next">${index < STEPS.length - 1 ? t("onboarding.next") : t("onboarding.finish")}</button>
    </div>
  `;

  tooltipEl!.setAttribute("aria-label", step.title());

  // Position tooltip near target
  if (target) {
    const rect = target.getBoundingClientRect();
    const tooltipRect = tooltipEl!.getBoundingClientRect();
    let top = rect.bottom + 12;
    let left = rect.left + rect.width / 2 - tooltipRect.width / 2;

    if (top + tooltipRect.height > window.innerHeight) {
      top = rect.top - tooltipRect.height - 12;
    }
    left = Math.max(8, Math.min(left, window.innerWidth - tooltipRect.width - 8));
    // Keep the whole tooltip — and its buttons — on screen even when the
    // target itself is not: the planner doesn't scroll to reach them.
    top = Math.max(8, Math.min(top, window.innerHeight - tooltipRect.height - 8));

    // dynamic: tooltip position is measured from getBoundingClientRect at runtime
    tooltipEl!.style.top = `${top}px`;
    tooltipEl!.style.left = `${left}px`;
  }

  // Bind actions
  tooltipEl!.querySelector<HTMLElement>(".onboarding-skip")!.onclick = finishOnboarding;
  tooltipEl!.querySelector<HTMLElement>(".onboarding-next")!.onclick = () => advanceStep();

  // If step has waitForAction, listen for the action on target
  if (step.waitForAction && target) {
    const handler = () => {
      advanceStep();
    };
    target.addEventListener(step.waitForAction, handler, { once: true });
    actionCleanup = () => {
      target.removeEventListener(step.waitForAction!, handler);
      if (step.highlightClass) {
        target.classList.remove(step.highlightClass);
      }
    };
  }
}

function advanceStep(): void {
  // Remove pulse from current step target
  const step = STEPS[currentStep];
  if (step?.highlightClass) {
    const target = document.querySelector(step.targetSelector);
    if (target) target.classList.remove(step.highlightClass);
  }

  // Save progress
  localStorage.setItem(PROGRESS_KEY, String(currentStep));

  currentStep++;
  showStep(currentStep);
}
